import { z } from "zod";

/**
 * The primitives every other schema is built from.
 *
 * They exist so that "a money field" means exactly one thing on both sides of
 * the wire: the editor clamps an input with the same bound the API rejects on,
 * and neither can drift without this file changing. See docs/06-api.md.
 */

/**
 * A cuid from the database, or a draft id the editor invented for a row that has
 * never been saved. The server never trusts a draft id — it mints a real one and
 * remaps every reference — so the only requirement here is that it is a short,
 * non-empty string that can be a key in a `mix` object.
 */
export const idSchema = z.string().trim().min(1).max(64);

/**
 * Generous by design. These are the bounds from docs/06-api.md#validation-rules,
 * chosen to catch a paste accident or a stray exponent rather than to express an
 * opinion about pricing.
 */
export const MAX_MONEY = 1e11;
export const MAX_COUNT = 1e6;

/**
 * Money arrives as a number and is stored as `Decimal(14, 2)`. Anything finer
 * than two decimals is rounded by the codec in src/server/lib/decimal.ts rather
 * than rejected here: a third decimal place is a rounding question, not a
 * malformed request, and refusing 19.999 would be a baffling error to receive.
 */
export const moneySchema = z
  .number({ invalid_type_error: "Enter an amount." })
  .finite()
  .min(0, "Amounts cannot be negative.")
  .max(MAX_MONEY, "That amount is implausibly large.");

export const countSchema = z
  .number({ invalid_type_error: "Enter a number of clients." })
  .int("Client counts are whole numbers.")
  .min(0, "Counts cannot be negative.")
  .max(MAX_COUNT, "That count is implausibly large.");

/** Percentages are stored as `Decimal(6, 3)`, so 0.125% survives a round trip. */
export const percentSchema = z
  .number({ invalid_type_error: "Enter a percentage." })
  .finite()
  .min(0, "Percentages cannot be negative.")
  .max(100, "Percentages cannot exceed 100.");

/** Units per client on a `PER_ACTIVE_UNIT_YEAR` line — a multiplier, not a share. */
export const unitsSchema = z.number().finite().min(0).max(MAX_COUNT);

/** ISO-4217. Upper-cased here so the product and its scenarios cannot disagree by case. */
export const currencyCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, "Use a three-letter currency code, such as USD or MVR.");

/** Timestamps cross the wire as ISO-8601 UTC strings (docs/06-api.md#conventions). */
export const isoDateTimeSchema = z.string().datetime();

/**
 * Enough of a user to render a name and an avatar next to a scenario. Emails are
 * included because the share and member dialogs address people by email.
 */
export const userRefSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  fullName: z.string(),
  nickName: z.string().nullable(),
});
export type UserRef = z.infer<typeof userRefSchema>;

/* ------------------------------------------------------------- collections */

export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;

/**
 * Query strings are always strings, so every numeric filter is coerced. An
 * unparseable `limit` falls back to the default rather than 400ing: a bad page
 * size in a bookmarked URL should show the first page, not an error.
 */
export const paginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).catch(0),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/** Every list endpoint answers in this envelope — docs/06-api.md#conventions. */
export function collectionSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().min(0),
    limit: z.number().int().min(1),
    offset: z.number().int().min(0),
  });
}

export interface Collection<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * A free-text search term. Trimmed, capped, and empty-means-absent so that
 * `?q=` and no `q` at all are the same request.
 */
export const searchQuerySchema = z
  .string()
  .trim()
  .max(120)
  .transform((value) => (value.length === 0 ? undefined : value))
  .optional();

/**
 * `?flag=true`. Present-but-empty counts as true so `?mine` behaves the way a
 * hand-typed URL suggests it should.
 */
export const booleanQuerySchema = z
  .union([z.literal("true"), z.literal("false"), z.literal("1"), z.literal("0"), z.literal("")])
  .transform((value) => value === "true" || value === "1" || value === "")
  .optional();
